export type HubWorkSalesLink = {
	id?: number;
	title: string;
	projectId: string;
	status: string;
	due: string;
	start?: string;
	salesOrderId?: number | string;
	workTypes?: string[];
	assignee?: string;
};

export type HubSalesOrderDoc = {
	id?: number;
	party: string;
	doc_at: string;
	ref_no: string;
	status: string;
	extra?: string;
	note: string;
	lines?: Array<{ description: string; amount: string }>;
};

export function salesOrderIdOf(value: number | string | undefined | null): number | null {
	if (value == null || value === "") return null;
	const id = Number(value);
	return Number.isFinite(id) ? id : null;
}

export function workMatchesSalesOrder(work: HubWorkSalesLink, salesOrderId: number): boolean {
	return salesOrderIdOf(work.salesOrderId) === salesOrderId;
}

export function worksForSalesOrder(work: HubWorkSalesLink[], salesOrderId: number): HubWorkSalesLink[] {
	return work.filter((row) => workMatchesSalesOrder(row, salesOrderId));
}

export function findSalesOrderById(orders: HubSalesOrderDoc[], salesOrderId: number): HubSalesOrderDoc | undefined {
	return orders.find((row) => row.id === salesOrderId);
}

/** Work that is unlinked or already linked to this sales order (safe to attach). */
export function workAvailableToLink(work: HubWorkSalesLink[], salesOrderId: number): HubWorkSalesLink[] {
	return work.filter((row) => {
		const linked = salesOrderIdOf(row.salesOrderId);
		return linked == null || linked === salesOrderId;
	});
}

export function workUnlinkedForNewLink(work: HubWorkSalesLink[]): HubWorkSalesLink[] {
	return work.filter((row) => salesOrderIdOf(row.salesOrderId) == null);
}

export function defaultWorkTitleFromSalesOrder(order: HubSalesOrderDoc): string {
	const line = order.lines?.[0]?.description?.split(/\n+/).map((part) => part.trim()).find(Boolean);
	return line || `Work for ${order.ref_no?.trim() || "sales order"}`;
}

export function defaultWorkDueFromSalesOrder(order: HubSalesOrderDoc): string {
	const due = order.extra?.trim().slice(0, 10) ?? "";
	if (/^\d{4}-\d{2}-\d{2}$/.test(due)) return due;
	const doc = order.doc_at?.trim().slice(0, 10) ?? "";
	return /^\d{4}-\d{2}-\d{2}$/.test(doc) ? doc : "";
}

export function salesOrderDetailPath(order: HubSalesOrderDoc): string {
	const key = encodeURIComponent(order.ref_no?.trim() || String(order.id ?? ""));
	return `/hub/sales-orders/${key}`;
}

export function workTemplateAlreadyLinked(order: HubSalesOrderDoc, linked: HubWorkSalesLink[]): boolean {
	const title = defaultWorkTitleFromSalesOrder(order).trim().toLowerCase();
	if (!title) return false;
	return linked.some((row) => row.title.trim().toLowerCase() === title);
}

export function workRecordWithSalesOrder(
	work: HubWorkSalesLink,
	salesOrderId: number | null,
): Record<string, unknown> {
	const record: Record<string, unknown> = { ...work };
	delete record.id;
	if (salesOrderId == null) delete record.salesOrderId;
	else record.salesOrderId = salesOrderId;
	return record;
}
